import { AnimatePresence, animate, motion } from "framer-motion";
import {
  AlertTriangle,
  Award,
  BarChart3,
  Building2,
  CheckCircle2,
  ChevronDown,
  Globe2,
  Landmark,
  Newspaper,
  Shield,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import BusinessScoreRadar from "src/common/charts/businessScoreRadar";
import AppContext from "src/components/AppContext";
import type { EquityResearchRecord } from "src/data/equityResearchTypes";
import {
  getScoreColor,
  getScoreDimensions,
  getScoreLabel,
  parseFactorList,
} from "src/data/equityResearchData";
import { fetchWrapper } from "src/helpers";

interface CompanyBusinessInsightProps {
  selectedCompany: string;
  onRecordLoaded?: (updatedAt: string) => void;
}

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.4, ease: "easeOut" as const },
  }),
};

type SectionDef = {
  id: string;
  menuLabel: string;
  number?: number;
  icon: React.ElementType;
  score?: number;
  showInNav?: boolean;
};

function toHighlights(text: string, max = 3): string[] {
  if (!text?.trim()) return [];
  return text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 30)
    .slice(0, max);
}

function ScoreGauge({ score, size = 108 }: { score: number; size?: number }) {
  const context = useContext(AppContext);
  const { darkMode } = context.state;
  const gradientId = React.useId().replace(/:/g, "");
  const clamped = Math.max(0, Math.min(10, score));
  const [displayScore, setDisplayScore] = useState(0);
  const [prevClamped, setPrevClamped] = useState(clamped);

  if (clamped !== prevClamped) {
    setDisplayScore(0);
    setPrevClamped(clamped);
  }

  const gaugeHeight = Math.round(size * 0.48);
  const cx = 50;
  const cy = 46;
  const radius = 38;
  const needleLen = radius * 0.72;
  const startAngle = Math.PI;
  const endAngle = Math.PI * (1 - clamped / 10);
  const startX = cx + needleLen * Math.cos(startAngle);
  const startY = cy - needleLen * Math.sin(startAngle);
  const endX = cx + needleLen * Math.cos(endAngle);
  const endY = cy - needleLen * Math.sin(endAngle);
  const color = getScoreColor(clamped);
  const track = darkMode ? "#334155" : "#e2e8f0";
  const labelColor = darkMode ? "#94a3b8" : "#64748b";
  const arcPath = `M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`;

  useEffect(() => {
    const controls = animate(0, clamped, {
      duration: 1.15,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (value) => setDisplayScore(value),
    });
    return () => controls.stop();
  }, [clamped]);

  return (
    <div
      className="relative flex shrink-0 flex-col items-center"
      style={{ width: size, minWidth: size }}
      aria-label={`Score ${clamped.toFixed(1)} out of 10`}
    >
      <motion.span
        key={clamped}
        className="mb-0.5 text-[15px] font-bold tabular-nums text-gray-900 dark:text-white"
        initial={{ opacity: 0.4 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {displayScore.toFixed(1)}
      </motion.span>
      <svg
        viewBox="0 0 100 52"
        width={size}
        height={gaugeHeight}
        className="overflow-visible"
        aria-hidden
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ef4444" />
            <stop offset="50%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#00A85F" />
          </linearGradient>
        </defs>
        <path
          d={arcPath}
          fill="none"
          stroke={track}
          strokeWidth={6}
          strokeLinecap="round"
        />
        <path
          d={arcPath}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={6}
          strokeLinecap="round"
          opacity={0.9}
        />
        <motion.line
          key={clamped}
          x1={cx}
          y1={cy}
          initial={{ x2: startX, y2: startY }}
          animate={{ x2: endX, y2: endY }}
          transition={{
            duration: 1.15,
            ease: [0.22, 1, 0.36, 1],
          }}
          stroke={color}
          strokeWidth={2.5}
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy} r={3.5} fill={color} />
      </svg>
      <div
        className="mt-0.5 flex w-full items-center justify-between px-0.5 text-[10px] font-semibold leading-none"
        style={{ color: labelColor }}
      >
        <span className="shrink-0">Poor</span>
        <span className="shrink-0">Excellent</span>
      </div>
    </div>
  );
}

function ExpandableText({
  text,
  previewChars = 220,
}: {
  text?: string | null;
  previewChars?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const safeText = text || "";
  const needsExpand = safeText.length > previewChars;

  return (
    <div>
      <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
        {needsExpand && !expanded
          ? `${safeText.slice(0, previewChars).trim()}…`
          : safeText}
      </p>
      {needsExpand && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
        >
          {expanded ? "Show less" : "Read full analysis"}
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      )}
    </div>
  );
}

function HighlightList({
  items,
  variant,
}: {
  items: string[];
  variant: "reward" | "risk";
}) {
  if (!items.length) return null;
  const Icon = variant === "reward" ? CheckCircle2 : AlertTriangle;
  const color =
    variant === "reward"
      ? "text-[#00A85F] dark:text-green-400"
      : "text-amber-500 dark:text-amber-400";

  return (
    <ul className="space-y-2.5">
      {items.map((item, i) => (
        <motion.li
          key={i}
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.08 }}
          className="flex gap-2.5 text-sm text-gray-700 dark:text-gray-300"
        >
          <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${color}`} />
          <span>{item}</span>
        </motion.li>
      ))}
    </ul>
  );
}

function FactorList({ factors }: { factors: string }) {
  const items = parseFactorList(factors);
  if (!items.length) return null;

  return (
    <ul className="mt-3 list-disc space-y-2 pl-4 text-sm leading-relaxed text-gray-600 dark:text-gray-300 marker:text-gray-400 dark:marker:text-gray-500">
      {items.map((item, i) => (
        <li key={i} className="pl-0.5">
          {item.value ? (
            <>
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                {item.label}:{" "}
              </span>
              <span>{item.value}</span>
            </>
          ) : (
            <span>{item.label}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function buildSections(record: EquityResearchRecord): SectionDef[] {
  return [
    {
      id: "overview",
      menuLabel: "Overview",
      icon: Sparkles,
      showInNav: false,
    },
    {
      id: "business",
      menuLabel: "What Does The Company Do?",
      number: 1,
      icon: Building2,
    },
    {
      id: "niche",
      menuLabel: "Niche & Business Uniqueness",
      number: 2,
      icon: Zap,
      score: Number(record.niche_score),
    },
    {
      id: "revenue",
      menuLabel: "Revenue Engine & Segments",
      number: 3,
      icon: TrendingUp,
      score: Number(record.segment_health_score),
    },
    {
      id: "moat",
      menuLabel: "Competitive Moat",
      number: 4,
      icon: Shield,
      score: Number(record.moat_strength_score),
    },
    {
      id: "competition",
      menuLabel: "Competitive Landscape",
      number: 5,
      icon: Users,
      score: Number(record.competitiveness_score),
    },
    {
      id: "demand",
      menuLabel: "Demand & Growth Runway",
      number: 6,
      icon: Globe2,
      score: Number(record.demand_score),
    },
    {
      id: "risk",
      menuLabel: "Risk vs Reward",
      number: 7,
      icon: Award,
      score: Number(record.risk_reward_score),
    },
    {
      id: "macro",
      menuLabel: "Macro & External Resilience",
      number: 8,
      icon: Landmark,
      score: Number(record.macro_resilience_score),
    },
    {
      id: "news",
      menuLabel: "Recent Developments",
      number: 9,
      icon: Newspaper,
    },
    {
      id: "verdict",
      menuLabel: "Analyst Verdict",
      icon: Sparkles,
      showInNav: false,
    },
  ];
}

function SectionBlock({
  id,
  sectionRef,
  number,
  icon: Icon,
  title,
  score,
  children,
  isFirst = false,
  animIndex = 0,
}: {
  id: string;
  sectionRef: (el: HTMLElement | null) => void;
  number?: number;
  icon: React.ElementType;
  title: string;
  score?: number;
  children: React.ReactNode;
  isFirst?: boolean;
  animIndex?: number;
}) {
  return (
    <motion.section
      id={id}
      ref={sectionRef}
      custom={animIndex}
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      className={`scroll-mt-3 ${isFirst ? "pt-2" : "pt-4"}`}
    >
      <motion.div
        whileHover={{ y: -2 }}
        transition={{ duration: 0.2 }}
        className=" bg-[#f8fafc] dark:bg-gray-900/90 p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow duration-300"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3 min-w-0">
            {number !== undefined ? (
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/30 text-[11px] font-semibold text-blue-600 dark:text-blue-400 tabular-nums">
                {number}
              </span>
            ) : (
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#d4fde5] dark:bg-[#0FA243]/20">
                <Icon className="w-3.5 h-3.5 text-[#00A85F] dark:text-green-400" />
              </span>
            )}
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white leading-snug">
                {title}
              </h3>
              {score !== undefined && !Number.isNaN(score) && (
                <p
                  className="text-xs mt-1"
                  style={{ color: getScoreColor(score) }}
                >
                  {score.toFixed(1)}/10 · {getScoreLabel(score)}
                </p>
              )}
            </div>
          </div>
          {score !== undefined && !Number.isNaN(score) && (
            <ScoreGauge score={score} size={108} />
          )}
        </div>
        <div>{children}</div>
      </motion.div>
    </motion.section>
  );
}

const CompanyBusinessInsight: React.FC<CompanyBusinessInsightProps> = ({
  selectedCompany,
  onRecordLoaded,
}) => {
  const context = useContext(AppContext);
  const { darkMode } = context.state;
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const navRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const isJumpingRef = useRef(false);
  const [activeSection, setActiveSection] = useState("");

  const [record, setRecord] = useState<EquityResearchRecord | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchRecord = async () => {
      if (!selectedCompany) {
        setRecord(null);
        return;
      }
      setLoading(true);
      try {
        const res = await fetchWrapper.get(
          `/api/dashboard/equity_research?symbol=${encodeURIComponent(selectedCompany)}`,
        );
        if (active) {
          setRecord(res?.data || null);
          if (res?.data?.updated_at) {
            onRecordLoaded?.(res.data.updated_at);
          }
        }
      } catch (err) {
        console.error(err);
        if (active) {
          setRecord(null);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    void fetchRecord();
    return () => {
      active = false;
    };
  }, [selectedCompany, onRecordLoaded]);

  useEffect(() => {
    setActiveSection("");
  }, [selectedCompany]);

  const { dimensions, average } = useMemo(
    () =>
      record ? getScoreDimensions(record) : { dimensions: [], average: 0 },
    [record],
  );

  const sections = useMemo(
    () => (record ? buildSections(record) : []),
    [record],
  );

  const navSections = useMemo(
    () => sections.filter((section) => section.showInNav !== false),
    [sections],
  );

  const scrollToSection = useCallback((id: string) => {
    const container = scrollRef.current;
    const el = sectionRefs.current[id];
    if (!container || !el) return;

    isJumpingRef.current = true;
    setActiveSection(id);

    const top =
      el.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop;

    container.scrollTo({ top: Math.max(0, top - 8), behavior: "smooth" });
    window.setTimeout(() => {
      isJumpingRef.current = false;
    }, 900);
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !record || navSections.length === 0) return;

    const updateActiveSection = () => {
      if (isJumpingRef.current) return;

      const containerRect = container.getBoundingClientRect();
      const viewportTop = containerRect.top;
      const nearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight <
        48;

      if (nearBottom) {
        const lastId = navSections[navSections.length - 1]?.id ?? "";
        setActiveSection((prev) => (prev === lastId ? prev : lastId));
        return;
      }

      // Overview is not in the sidebar — clear highlight until a nav section
      // has scrolled up past the activation line (not merely peeking in).
      const overviewEl = sectionRefs.current.overview;
      if (overviewEl) {
        const overviewRect = overviewEl.getBoundingClientRect();
        const overviewVisibleBottom = Math.min(
          overviewRect.bottom,
          containerRect.bottom,
        );
        const overviewVisibleTop = Math.max(overviewRect.top, viewportTop);
        const overviewVisible = Math.max(
          0,
          overviewVisibleBottom - overviewVisibleTop,
        );
        if (overviewVisible > container.clientHeight * 0.35) {
          setActiveSection((prev) => (prev === "" ? prev : ""));
          return;
        }
      }

      const activationLine = viewportTop + 120;
      let currentId = "";

      for (const { id } of navSections) {
        const el = sectionRefs.current[id];
        if (!el) continue;

        const rect = el.getBoundingClientRect();
        if (rect.top <= activationLine) {
          currentId = id;
        }
      }

      setActiveSection((prev) => (prev === currentId ? prev : currentId));
    };

    container.addEventListener("scroll", updateActiveSection, {
      passive: true,
    });
    updateActiveSection();

    return () => container.removeEventListener("scroll", updateActiveSection);
  }, [navSections, record]);

  useEffect(() => {
    if (!activeSection) return;
    const navButton = navRefs.current[activeSection];
    navButton?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [activeSection]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[320px] px-6 text-center">
        <div className="w-8 h-8 border-4 border-[#00A85F] border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
          Loading business insight...
        </p>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[320px] px-6 text-center">
        <Building2 className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
        <p className="text-lg font-semibold text-gray-700 dark:text-gray-200">
          Business research not available yet
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 max-w-md">
          Deep-dive analysis for <strong>{selectedCompany}</strong> is coming
          soon.
        </p>
      </div>
    );
  }

  const competitors = record.competitors
    ?.split(",")
    .map((c) => c.trim())
    .filter(Boolean);

  const rewards = [
    ...dimensions
      .filter((d) => d.score >= 7)
      .map(
        (d) =>
          `${d.label} scores ${d.score.toFixed(1)}/10 — ${getScoreLabel(d.score).toLowerCase()} position`,
      ),
    ...toHighlights(record.demand_analysis, 1),
  ].slice(0, 4);

  const risks = [
    ...dimensions
      .filter((d) => d.score < 5)
      .map(
        (d) => `${d.label} scores ${d.score.toFixed(1)}/10 — area of concern`,
      ),
    ...toHighlights(record.risk_analysis, 2),
  ].slice(0, 4);

  const setSectionRef = (id: string) => (el: HTMLElement | null) => {
    sectionRefs.current[id] = el;
  };

  return (
    <div className="h-full min-h-0 flex flex-col lg:flex-row bg-[#f8fafc] dark:bg-[#121419]">
      <aside className="shrink-0 lg:w-52 xl:w-60 lg:border-r border-[#d0d4e4] dark:border-[#394564] bg-[#f8fafc] dark:bg-gray-900/90 overflow-x-auto lg:overflow-y-auto custom-scroll">
        <div className="p-2 lg:py-4 lg:px-2">
          <nav className="flex lg:flex-col gap-0.5 min-w-max lg:min-w-0">
            {navSections.map((section) => {
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  ref={(el) => {
                    navRefs.current[section.id] = el;
                  }}
                  type="button"
                  aria-current={isActive ? "true" : undefined}
                  onClick={() => scrollToSection(section.id)}
                  className={`relative flex items-start gap-2 px-2.5 py-2 rounded-md text-left text-[11px] leading-snug font-medium transition-all cursor-pointer whitespace-nowrap lg:whitespace-normal w-full ${
                    isActive
                      ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200 shadow-sm ring-1 ring-blue-300/70 dark:ring-blue-600/50"
                      : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  }`}
                >
                  {isActive && (
                    <span className="absolute left-0 top-1 bottom-1 w-[3px] rounded-full bg-blue-600 dark:bg-blue-400" />
                  )}
                  {section.number !== undefined ? (
                    <span
                      className={`mt-0.5 text-xs tabular-nums shrink-0 w-5 font-semibold ${
                        isActive
                          ? "text-blue-600 dark:text-blue-400"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {section.number}
                    </span>
                  ) : (
                    <section.icon
                      className={`mt-0.5 w-3.5 h-3.5 shrink-0 ${isActive ? "text-blue-500" : "opacity-50"}`}
                    />
                  )}
                  <span className="flex-1 min-w-0 lg:line-clamp-2 lg:whitespace-normal">
                    {section.menuLabel}
                  </span>
                  {section.score !== undefined && (
                    <span
                      className="mt-0.5 text-xs font-bold tabular-nums shrink-0"
                      style={{ color: getScoreColor(section.score) }}
                    >
                      {section.score.toFixed(0)}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </aside>

      <main
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto custom-scroll bg-[#f8fafc] dark:bg-gray-900/90"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-4 pb-8 space-y-4">
          {/* Overview */}
          <SectionBlock
            id="overview"
            sectionRef={setSectionRef("overview")}
            icon={Sparkles}
            title={`${record.company_name}`}
            score={average}
            isFirst
          >
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
              {(record.core_business_mechanics || "").slice(0, 200)}…{" "}
              <button
                type="button"
                onClick={() => scrollToSection("business")}
                className="text-blue-600 dark:text-blue-400 font-medium hover:underline cursor-pointer"
              >
                More details
              </button>
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-4">
              <div className="flex flex-col rounded-lg bg-[#f4f5f8] dark:bg-[#30324e]/30 p-3 border border-[#d0d4e4]/50 dark:border-[#394564]/50">
                <div className="mb-2">
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-1 flex items-center gap-2">
                    <Target className="w-4 h-4 text-blue-500" />
                    Business Health Snapshot
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Seven dimensions scored out of 10 — inspired by
                    institutional equity research
                  </p>
                </div>
                <div className="flex-1 flex items-center justify-center w-full">
                  <BusinessScoreRadar
                    dimensions={dimensions}
                    darkMode={darkMode}
                  />
                </div>
              </div>

              <div className="flex flex-col rounded-lg bg-[#f4f5f8] dark:bg-[#30324e]/30 p-3 border border-[#d0d4e4]/50 dark:border-[#394564]/50">
                <div className="mb-3">
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-white flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-emerald-500" />
                    Score Breakdown
                  </h3>
                </div>
                <div className="flex flex-col gap-y-1.5 flex-1 justify-center">
                  {dimensions.map((d) => (
                    <div key={d.key} className="flex items-center gap-3">
                      <span className="text-[11px] text-gray-500 dark:text-gray-400 w-20 shrink-0 truncate">
                        {d.shortLabel}
                      </span>
                      <div className="h-1.5 flex-1 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          style={{ backgroundColor: getScoreColor(d.score) }}
                          initial={{ width: 0 }}
                          whileInView={{ width: `${(d.score / 10) * 100}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.6 }}
                        />
                      </div>
                      <span className="text-[11px] font-semibold dark:text-white w-6 text-right tabular-nums">
                        {d.score.toFixed(0)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 pt-3 border-t border-[#d0d4e4] dark:border-[#394564]">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-2.5">
                  Rewards
                </p>
                <HighlightList items={rewards} variant="reward" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-2.5">
                  Risk Analysis
                </p>
                <HighlightList items={risks} variant="risk" />
              </div>
            </div>
          </SectionBlock>

          <SectionBlock
            id="business"
            sectionRef={setSectionRef("business")}
            number={1}
            icon={Building2}
            title="What Does The Company Do?"
            animIndex={1}
          >
            <ExpandableText
              text={record.core_business_mechanics}
              previewChars={280}
            />
          </SectionBlock>

          <SectionBlock
            id="niche"
            sectionRef={setSectionRef("niche")}
            number={2}
            icon={Zap}
            title="Niche & Business Uniqueness"
            score={Number(record.niche_score)}
            animIndex={2}
          >
            <HighlightList
              items={toHighlights(record.niche_analysis, 2)}
              variant="reward"
            />
            <div className="mt-3">
              <ExpandableText text={record.niche_analysis} />
            </div>
          </SectionBlock>

          <SectionBlock
            id="revenue"
            sectionRef={setSectionRef("revenue")}
            number={3}
            icon={TrendingUp}
            title="Revenue Engine & Segments"
            score={Number(record.segment_health_score)}
            animIndex={3}
          >
            <ExpandableText text={record.revenue_analysis} previewChars={240} />
            <FactorList factors={record.revenue_factors} />
          </SectionBlock>

          <SectionBlock
            id="moat"
            sectionRef={setSectionRef("moat")}
            number={4}
            icon={Shield}
            title="Competitive Moat"
            score={Number(record.moat_strength_score)}
            animIndex={4}
          >
            <ExpandableText text={record.moat_analysis} previewChars={240} />
            <FactorList factors={record.moat_factors} />
          </SectionBlock>

          <SectionBlock
            id="competition"
            sectionRef={setSectionRef("competition")}
            number={5}
            icon={Users}
            title="Competitive Landscape"
            score={Number(record.competitiveness_score)}
            animIndex={5}
          >
            {competitors && competitors.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {competitors.map((name, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className="px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800/40"
                  >
                    {name}
                  </motion.span>
                ))}
              </div>
            )}
            <ExpandableText text={record.competitor_analysis} />
          </SectionBlock>

          <SectionBlock
            id="demand"
            sectionRef={setSectionRef("demand")}
            number={6}
            icon={Globe2}
            title="Demand & Growth Runway"
            score={Number(record.demand_score)}
            animIndex={6}
          >
            <HighlightList
              items={toHighlights(record.demand_analysis, 2)}
              variant="reward"
            />
            <div className="mt-3">
              <ExpandableText text={record.demand_analysis} />
            </div>
          </SectionBlock>

          <SectionBlock
            id="risk"
            sectionRef={setSectionRef("risk")}
            number={7}
            icon={Award}
            title="Risk vs Reward"
            score={Number(record.risk_reward_score)}
            animIndex={7}
          >
            <HighlightList
              items={toHighlights(record.risk_analysis, 2)}
              variant="risk"
            />
            <div className="mt-3">
              <ExpandableText text={record.risk_analysis} />
            </div>
          </SectionBlock>

          <SectionBlock
            id="macro"
            sectionRef={setSectionRef("macro")}
            number={8}
            icon={Landmark}
            title="Macro & External Resilience"
            score={Number(record.macro_resilience_score)}
            animIndex={8}
          >
            <ExpandableText text={record.macro_analysis} previewChars={240} />
            <FactorList factors={record.macro_factors} />
          </SectionBlock>

          <SectionBlock
            id="news"
            sectionRef={setSectionRef("news")}
            number={9}
            icon={Newspaper}
            title="Recent Developments"
            animIndex={9}
          >
            <ExpandableText text={record.recent_news} previewChars={260} />
          </SectionBlock>

          <motion.section
            id="verdict"
            ref={setSectionRef("verdict")}
            custom={10}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="scroll-mt-3"
          >
            <div className="rounded-lg border border-[#d0d4e4] dark:border-[#394564] bg-gradient-to-br from-blue-50/80 to-[#d4fde5]/40 dark:from-blue-900/10 dark:to-[#0FA243]/10 p-5 sm:p-6 text-center shadow-sm">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">
                Analyst Verdict
              </p>
              <AnimatePresence>
                <motion.p
                  key={record.analyst_verdict}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-base sm:text-lg font-medium text-gray-800 dark:text-gray-100 leading-relaxed max-w-2xl mx-auto"
                >
                  {record.analyst_verdict}
                </motion.p>
              </AnimatePresence>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                Aggregate score{" "}
                <span
                  className="font-semibold"
                  style={{ color: getScoreColor(average) }}
                >
                  {average.toFixed(2)} / 10
                </span>
                <span className="text-gray-400"> · </span>
                {getScoreLabel(average)}
              </p>
            </div>
          </motion.section>
        </div>
      </main>
    </div>
  );
};

export default React.memo(CompanyBusinessInsight);
